import { Auth } from "aws-amplify";
import { message } from "antd";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/Components/ui/button";
import { Input } from "@/Components/ui/input";
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from "@/Components/ui/field";
import { emailPattern } from "Utility/Constants";
import {
  clearGoogleAccountLinkRetryAttempt,
  clearGoogleOAuthPending,
  clearOAuthInvalidGrantSessionFlag,
  getFederatedSignInFailureUserMessage,
  getSignupErrorMessage,
  setGoogleOAuthPending,
  stripOAuthCallbackParamsFromUrl,
} from "Utility/authErrors";
import Logo from "@/Assets/Logo/logo.png";
import AuthImage from "@/Assets/auth-image.png";
import { Loader2 } from "lucide-react";

function Signup() {
  const navigate = useNavigate();
  const [isSignupLoading, setIsSignupLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const isFormDisabled = isSignupLoading || isGoogleLoading;

  const handleSubmit = async (e) => {
    e.preventDefault();

    const form = e.target;
    const email = form.email?.value?.trim();
    const password = form.password?.value || "";

    if (!email) {
      form.email?.focus();
      return;
    }

    if (!emailPattern.test(email)) {
      form.email?.setCustomValidity("Please enter a valid email");
      form.email?.reportValidity();
      return;
    }

    form.email?.setCustomValidity("");

    if (!password) {
      form.password?.focus();
      return;
    }

    if (password.length < 8) {
      form.password?.setCustomValidity("Password must be minimum 8 characters.");
      form.password?.reportValidity();
      return;
    }

    form.password?.setCustomValidity("");

    setIsSignupLoading(true);
    clearGoogleOAuthPending();
    try {
      await Auth.signUp({
        username: email,
        password,
        attributes: {
          email,
          profile: "false",
        },
        autoSignIn: {
          enabled: true,
        },
      });

      try {
        await Auth.currentAuthenticatedUser();
      } catch (_) {
        await Auth.signIn(email, password);
      }

      navigate("/onboarding", { replace: true });
    } catch (err) {
      message.error(getSignupErrorMessage(err));
    } finally {
      setIsSignupLoading(false);
    }
  };

  const handleContinueWithGoogle = async () => {
    clearGoogleAccountLinkRetryAttempt();
    clearOAuthInvalidGrantSessionFlag();
    stripOAuthCallbackParamsFromUrl();
    setGoogleOAuthPending();
    setIsGoogleLoading(true);
    try {
      await Auth.federatedSignIn({ provider: "Google" });
    } catch (err) {
      message.error(getFederatedSignInFailureUserMessage(err));
      setIsGoogleLoading(false);
      clearGoogleOAuthPending();
    }
  };

  return (
    <div className="grid min-h-svh lg:grid-cols-2">
      <div className="flex flex-col gap-4 p-6 md:p-10">
        <div className="flex justify-center gap-2 md:justify-start">
          <Link
            to="/"
            className="flex items-center gap-2 font-semibold"
            aria-label="Inprofile home"
          >
            <img src={Logo} alt="Inprofile" className="h-8 w-8" />
            Inprofile
          </Link>
        </div>
        <div className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-xs">
            <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
              <FieldGroup className={"gap-4"}>
                <div className="flex flex-col items-center gap-1 text-center">
                  <h1 className="text-2xl font-bold mb-4">Create your account</h1>
                </div>
                <Field>
                  <FieldLabel htmlFor="email">Email</FieldLabel>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="john@example.com"
                    required
                    autoComplete="email"
                    disabled={isFormDisabled}
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="password">Password</FieldLabel>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    required
                    minLength={8}
                    autoComplete="new-password"
                    disabled={isFormDisabled}
                  />
                </Field>
                <Field>
                  <Button type="submit" className="w-full" disabled={isFormDisabled}>
                    {isSignupLoading ? (
                      <>
                        <Loader2 className="mr-2 size-4 animate-spin" />
                        Creating account...
                      </>
                    ) : (
                      "Create Account"
                    )}
                  </Button>
                </Field>
                <Field>
                  <Button
                    variant="outline"
                    type="button"
                    className="w-full"
                    onClick={handleContinueWithGoogle}
                    disabled={isFormDisabled}
                  >
                    {isGoogleLoading ? (
                      <Loader2 className="mr-2 size-4 animate-spin" />
                    ) : (
                      <GoogleIcon className="mr-2 size-4" />
                    )}
                    Continue with Google
                  </Button>
                  <FieldDescription className="text-center">
                    Already have an account?{" "}
                    <Link
                      to="/login"
                      className="underline underline-offset-4 text-primary"
                    >
                      Login
                    </Link>
                  </FieldDescription>
                </Field>
              </FieldGroup>
            </form>
          </div>
        </div>
      </div>
      <div className="bg-[#f2f5f8] relative hidden lg:block">
        <img
          src={AuthImage}
          alt=""
          className="absolute inset-0 h-full w-full object-contain dark:brightness-[0.2] dark:grayscale"
        />
      </div>
    </div>
  );
}

function GoogleIcon({ className = "", ...props }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      className={className}
      {...props}
    >
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

export default Signup;
